import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import './index.scss';
import { useUsers } from '@/Recoil/User';
import { useCharacter } from '@/Recoil/World';
import { navigateTo } from '@/Utils/routing';
import EditableFileField from '@/Components/EditableFileField';
import ErrorSnackbar from '@/Components/Product/ErrorSnackbar';
import IsometricRoom from '@/Components/IsometricRoom/IsometricRoom';
import { uploadImageToS3 } from '@/Data/Image';
import LoadingCircle from '@/Components/LoadingCircle';
import { logBehavior } from '@/Data/Behavior';
import { useKoreanStudyTypeContext } from '../KoreanStudyTypeContext';

const avatarSets = [
    { sample: '/img/sample_02.png', avatar: '/img/avatar_sample_02.png' },
    { sample: '/img/sample_03.png', avatar: '/img/avatar_sample_03.png' },
    { sample: '/img/sample_04.png', avatar: '/img/avatar_sample_04.png' },
    { sample: '/img/sample_05.jpg', avatar: '/img/avatar_sample_05.png' },
    { sample: '/img/sample_01.png', avatar: '/img/avatar_sample_01.png' },
];

interface Props {
    path?: string;
}

const KoreanStudyTypeMakeAvatar: React.FC<Props> = () => {
    const {
        addressid,
        typeid,
        characterPic,
        characterPollingStatus,
        characterError,
        startCharacterGeneration,
        cancelCharacterGeneration,
        roomPic,
        roomPicPollingStatus,
        roomPicError,
        startRoomPicGeneration,
        cancelRoomPicGeneration,
    } = useKoreanStudyTypeContext();

    const { profiles, syncProfiles } = useUsers();
    const { fetchCharacter, characters } = useCharacter();

    const [isLoading, setIsLoading] = useState(false);
    const roomRef = useRef<HTMLDivElement>(null);
    const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    // Track if room pic generation was ever in progress (persists through refresh via polling status)
    const wasGeneratingRoomPic = useRef(false);

    useEffect(() => {
        if (addressid) {
            syncProfiles([addressid]);
            fetchCharacter(addressid);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressid]);

    const profile = profiles[addressid];
    const profileImage = profile?.image;
    const existingCharacterImage = characterPic || characters[addressid]?.image;
    const sourceImage = uploadedImageUrl || profileImage;

    const isGeneratingCharacter = characterPollingStatus === 'polling';
    const isGeneratingRoomPic = roomPicPollingStatus === 'polling';

    // Track when room pic generation starts (including from restored polling state)
    useEffect(() => {
        if (isGeneratingRoomPic) {
            wasGeneratingRoomPic.current = true;
        }
    }, [isGeneratingRoomPic]);

    type Step = 'submit' | 'generating' | 'homePictureGenerating';
    const step: Step = isGeneratingRoomPic
        ? 'homePictureGenerating'
        : isGeneratingCharacter
            ? 'generating'
            : 'submit';

    const hookError = characterError || roomPicError;

    // Navigate to result when roomPic is completed
    useEffect(() => {
        if (wasGeneratingRoomPic.current && roomPicPollingStatus === 'completed' && roomPic) {
            navigateTo(`/personatest/koreanstudytype/result/${addressid}/${typeid}`);
        }
    }, [roomPicPollingStatus, roomPic, addressid, typeid]);

    // Show error snackbar when roomPic fails
    useEffect(() => {
        if (roomPicPollingStatus === 'failed' && roomPicError) {
            setSnackbarMessage('Gagal membuat Home Picture. Silakan coba lagi.');
            setShowSnackbar(true);
            setTimeout(() => setShowSnackbar(false), 5000);
        }
    }, [roomPicPollingStatus, roomPicError]);

    const handleStartGeneration = async () => {
        logBehavior('personatest:251212kst:button_press:start_avatar_generation');
        if (!sourceImage) { return; }
        await startCharacterGeneration(sourceImage);
    };

    const handleRetry = () => {
        cancelCharacterGeneration();
        cancelRoomPicGeneration();
    };

    const handleStartHomePictureGeneration = async () => {
        logBehavior('personatest:251212kst:button_press:start_home_picture_generation');
        if (!existingCharacterImage || !roomRef.current) { return; }

        try {
            setIsLoading(true);
            wasGeneratingRoomPic.current = true;

            const canvas = await html2canvas(roomRef.current, {
                useCORS: true,
                allowTaint: true,
                backgroundColor: 'white',
                scale: 3,
            });

            const dataUrl = canvas.toDataURL('image/png');
            const roomImageUrl = await uploadImageToS3(dataUrl, `room_${addressid}_${Date.now()}.png`);

            setIsLoading(false);

            if (!roomImageUrl) {
                wasGeneratingRoomPic.current = false;
                setSnackbarMessage('Gagal mengupload gambar room.');
                setShowSnackbar(true);
                setTimeout(() => setShowSnackbar(false), 5000);
                return;
            }

            await startRoomPicGeneration(roomImageUrl);
        } catch (err) {
            console.error('Failed to capture room:', err);
            wasGeneratingRoomPic.current = false;
            setIsLoading(false);
            setSnackbarMessage('Gagal mengambil gambar room.');
            setShowSnackbar(true);
            setTimeout(() => setShowSnackbar(false), 5000);
        }
    };

    // Animation state
    const [animPhase, setAnimPhase] = useState(0);
    const [currentSet, setCurrentSet] = useState(0);
    const [fadeClass, setFadeClass] = useState('fade-in');

    useEffect(() => {
        if (step !== 'generating' && step !== 'homePictureGenerating') { return; }

        const delays = [1000, 1500];
        const timer = setTimeout(() => {
            setFadeClass('fade-out');
            setTimeout(() => {
                if (animPhase === 1) {
                    setCurrentSet((prev) => (prev + 1) % avatarSets.length);
                    setAnimPhase(0);
                } else {
                    setAnimPhase(1);
                }
                setFadeClass('fade-in');
            }, 300);
        }, delays[animPhase]);

        return () => clearTimeout(timer);
    }, [animPhase, currentSet, step]);

    const renderSubmitStep = () => (
        <>
            <div className="content-section">
                <div className="title-text">
                    <p>{"Pilih foto untuk membuat avatar"}</p>
                </div>

                {existingCharacterImage && (
                    <div className="existing-character">
                        <p className="existing-label">{"Avatar kamu saat ini:"}</p>
                        <img src={existingCharacterImage} alt="Current Avatar" className="existing-avatar" />
                    </div>
                )}

                <div className="upload-section">
                    <p className="upload-label">
                        {existingCharacterImage ? 'Buat avatar baru:' : 'Upload foto wajahmu:'}
                    </p>
                    <EditableFileField
                        value={uploadedImageUrl || profileImage || null}
                        onChange={setUploadedImageUrl}
                        accept="image/jpeg,image/png,image/jpg"
                    />
                </div>

                <div className="description-section">
                    <p className="description-note">
                        {"Untuk hasil avatar terbaik, upload foto bersama saya atau teman-teman.\nBoleh juga upload foto grup dengan beberapa orang."}
                    </p>
                </div>

                {!existingCharacterImage && (
                    <div className="examples-section">
                        <p className="examples-title">{"Contoh hasil avatar:"}</p>
                        <div className="examples-rolling">
                            <div className="examples-track">
                                {[...avatarSets, ...avatarSets].map((set, idx) => (
                                    <div className="example-pair" key={idx}>
                                        <img src={set.sample} alt="Sample" className="example-sample" />
                                        <div className="arrow">→</div>
                                        <img src={set.avatar} alt="Avatar" className="example-avatar" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="cta-section">
                <div className="cta-wrapper">
                    {existingCharacterImage && (
                        <button className="btn-primary" onClick={handleStartHomePictureGeneration}>
                            {'Gunakan karakter ini'}
                        </button>
                    )}
                    <button className="btn-primary" onClick={handleStartGeneration} disabled={!sourceImage}>
                        {existingCharacterImage ? 'Buat Avatar Baru' : 'Buat Avatar'}
                    </button>
                </div>
            </div>
        </>
    );

    const renderGeneratingStep = () => (
        <>
            <div className="content-section">
                <div className="title-text pulse-effect">
                    <p>{`Sedang membuat avatar yang pas untuk ${profile?.name ?? ''}`}</p>
                </div>

                <div className={`lottie-container ${fadeClass}`}>
                    {animPhase === 0 && (
                        <img
                            src={avatarSets[currentSet].sample}
                            alt="Sample"
                            style={{ width: 128, height: 128, borderRadius: '24px', objectFit: 'cover' }}
                        />
                    )}
                    {animPhase === 1 && (
                        <img
                            src={avatarSets[currentSet].avatar}
                            alt="Avatar"
                            style={{ width: 128, height: 128, objectFit: 'contain' }}
                        />
                    )}
                </div>

                <div className="icons-grid">
                    <div className="icons-row">
                        <div className="icon-badge blue"><img src="/img/type_skill_hunter.svg" alt="Skill" /></div>
                        <div className="icon-badge green"><img src="/img/type_si_calon_traveler.svg" alt="Travel" /></div>
                        <div className="icon-badge pink"><img src="/img/type_k_content_lover.svg" alt="K-Contents" /></div>
                        <div className="icon-badge purple"><img src="/img/type_social_butterfly.svg" alt="Social" /></div>
                    </div>
                    <div className="icons-row offset">
                        <div className="icon-badge yellow"><img src="/img/type_si_akademis.svg" alt="Book" /></div>
                        <div className="icon-badge teal"><img src="/img/type_casual_learner.svg" alt="Casual" /></div>
                        <div className="icon-badge orange"><img src="/img/type_si_idol_stan.svg" alt="Idol" /></div>
                        <div className="icon-badge navy"><img src="/img/type_si_paling_estetik.svg" alt="Gamsung" /></div>
                    </div>
                </div>

                <div className="description-section">
                    <p className="description-title">{"Sedang membuat Avatar..."}</p>
                    <p className="description-note">{"Proses pembuatan avatar memakan waktu sekitar 30 detik"}</p>
                </div>

                {hookError && (
                    <div className="error-section">
                        <p className="error-text">{hookError}</p>
                        <button className="btn-retry" onClick={handleRetry}>{"Coba Lagi"}</button>
                    </div>
                )}
            </div>

            <div className="cta-section">
                <div className="cta-wrapper">
                    <button className="btn-primary" disabled>{"Membuat Avatar..."}</button>
                </div>
            </div>
        </>
    );

    const renderHomePictureGeneratingStep = () => (
        <>
            <div className="content-section">
                <div className="title-text pulse-effect">
                    <p>{`Sedang menempatkan avatar di kamar ${profile?.name ?? ''}`}</p>
                </div>

                <div className={`lottie-container home-picture-swap ${fadeClass}`}>
                    <div className="home-picture-preview" style={animPhase !== 0 ? { display: 'none' } : {}}>
                        <IsometricRoom addressid={addressid} />
                    </div>
                    <img
                        src={existingCharacterImage}
                        alt="Your Avatar"
                        style={{
                            width: "100%", maxWidth: 470,
                            aspectRatio: '470 / 540', objectFit: 'cover',
                            ...(animPhase !== 1 ? { display: 'none' } : {})
                        }}
                    />
                </div>

                <div className="description-section">
                    <p className="description-title">{"Sedang membuat Home Picture..."}</p>
                    <p className="description-note">{"Proses ini memakan waktu sekitar 30 detik"}</p>
                </div>

                {hookError && (
                    <div className="error-section">
                        <p className="error-text">{hookError}</p>
                        <button className="btn-retry" onClick={handleRetry}>{"Coba Lagi"}</button>
                    </div>
                )}
            </div>

            <div className="cta-section">
                <div className="cta-wrapper">
                    <button className="btn-primary" disabled>{"Membuat Home Picture..."}</button>
                </div>
            </div>
        </>
    );

    const renderStep = () => {
        switch (step) {
            case 'submit':                return renderSubmitStep();
            case 'generating':            return renderGeneratingStep();
            case 'homePictureGenerating': return renderHomePictureGeneratingStep();
        }
    };

    return (
        <div className="korean-study-type-madeavatar-container">
            <div className="top-section">
                <div className="logo-wrapper">
                    <div className="logo-icon"><img src="/img/matelogoguide.png" alt="MATE Logo" /></div>
                    <span className="logo-text">{"MATE"}</span>
                </div>
                <div className="header-section">
                    <p className="header-title">{"Korean Language Personality Test"}</p>
                </div>
            </div>

            {renderStep()}

            <ErrorSnackbar show={showSnackbar} message={snackbarMessage} />

            <div
                className={'IsometricRoomHolder'}
                ref={roomRef}
                style={{
                    position: 'absolute', left: -9999, top: 0, pointerEvents: 'none',
                    width: 540, height :540,
                }}
            >
                <IsometricRoom addressid={addressid} />
            </div>
            <LoadingCircle show={isLoading} />
        </div>
    );
};

export default KoreanStudyTypeMakeAvatar;
